import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Info, AlertTriangle, Check } from 'lucide-react';
import { API } from '@/contexts/AuthContext';

/**
 * JCEV2InlineHints - Compact inline hints for pricing rows
 * 
 * Shows 2-4 short hints inside a job group card based on
 * the repair intent. These are compact reminders, not full guidance.
 */

// Repair intent mapping - must match JCEV2Panel.js
const REPAIR_INTENT_MAPPING = {
  // === BRAKES ===
  "front_brake_pads": "BRAKE_FRONT_OVERHAUL",
  "front_brake_discs": "BRAKE_FRONT_OVERHAUL",
  "rear_brake_pads": "BRAKE_REAR_OVERHAUL",
  "rear_brake_discs": "BRAKE_REAR_OVERHAUL",
  "handbrake_shoes": "BRAKE_REAR_OVERHAUL",
  "brake_hoses_lines": "BRAKE_FRONT_OVERHAUL",
  "parking_brake_operation": "BRAKE_REAR_OVERHAUL",
  // === BRAKE FLUID ===
  "brake_fluid": "BRAKE_FLUID_SERVICE",
  "brake_fluid_condition": "BRAKE_FLUID_SERVICE",
  "brake_fluid_boiling_point": "BRAKE_FLUID_SERVICE",
  "brake_fluid_boiling_point_test": "BRAKE_FLUID_SERVICE",
  // === SUSPENSION - FRONT ===
  "front_springs": "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  "front_springs_ns": "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  "front_springs_os": "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  "front_shock_absorbers": "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  "front_shock_absorbers_ns": "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  "front_shock_absorbers_os": "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  "front_top_mounts": "SUSPENSION_FRONT_TOP_MOUNT_REPLACEMENT",
  "front_lower_arm": "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "anti_roll_bar_links": "SUSPENSION_FRONT_DROP_LINK_REPLACEMENT",
  "ball_joints": "SUSPENSION_FRONT_ARM_REPLACEMENT",
  "steering_rack": "SUSPENSION_FRONT_TRACK_ROD_REPLACEMENT",
  "track_rod_ends": "SUSPENSION_FRONT_TRACK_ROD_REPLACEMENT",
  // === SUSPENSION - REAR ===
  "rear_springs": "SUSPENSION_REAR_SPRING_REPLACEMENT",
  "rear_shock_absorbers": "SUSPENSION_REAR_SHOCK_REPLACEMENT",
  "rear_anti_roll_bar_links": "SUSPENSION_REAR_SHOCK_REPLACEMENT",
  "subframe_rear": "SUBFRAME_REAR_REPLACEMENT",
  "wheel_bearings": "SUSPENSION_FRONT_WHEEL_BEARING_REPLACEMENT",
  // === DRIVETRAIN ===
  "clutch": "CLUTCH_SYSTEM_REPLACEMENT",
  "cv_joints_boots": "CV_BOOT_REPLACEMENT",
  "timing_belt": "TIMING_BELT_JOB",
  // === DRIVE BELTS ===
  "drive_belts": "DRIVE_BELT_REPLACEMENT",
  "v_belt": "DRIVE_BELT_REPLACEMENT",
  "v_belts": "DRIVE_BELT_REPLACEMENT",
  "v-belt": "DRIVE_BELT_REPLACEMENT",
  "aux_belt": "DRIVE_BELT_REPLACEMENT",
  // === ELECTRICAL ===
  "battery": "BATTERY_REPLACEMENT",
  "battery_test": "BATTERY_REPLACEMENT",
  "dash_warning_lamps": "DIAGNOSTIC_INVESTIGATION",
  "warning_lights": "DIAGNOSTIC_INVESTIGATION",
  // === TYRES ===
  "tyre": "TYRE_REPLACEMENT",
  "tyre_fl": "TYRE_REPLACEMENT",
  "tyre_fr": "TYRE_REPLACEMENT",
  "tyre_rl": "TYRE_REPLACEMENT",
  "tyre_rr": "TYRE_REPLACEMENT",
  "front_tyre": "TYRE_REPLACEMENT",
  "rear_tyre": "TYRE_REPLACEMENT",
  "spare_tyre": "TYRE_REPLACEMENT",
  // === COOLANT ===
  "coolant": "COOLANT_SERVICE",
  "coolant_freeze_point_test": "COOLANT_SERVICE",
  "hoses_connections": "COOLANT_SERVICE",
  // === ENGINE ===
  "engine_oil_level_condition": "ENGINE_OIL_SERVICE",
  "engine_transmission_leaks": "ENGINE_OIL_LEAK_REPAIR",
  // === EXHAUST ===
  "exhaust_system": "EXHAUST_REPAIR",
  "catalytic_converter": "CATALYTIC_CONVERTER_REPLACEMENT",
  "dpf_condition": "DPF_SERVICE",
  // === AIR CON ===
  "air_con": "AIRCON_SERVICE",
  "aircon": "AIRCON_SERVICE",
  "aircon_operation": "AIRCON_SERVICE",
  // === HEATER ===
  "heater_fan_controls": "HEATER_SERVICE",
  // === STRUCTURAL ===
  "underside_corrosion": "WELDING_STRUCTURAL",
  // === WHEEL ALIGNMENT (iter198 — mirror backend mapping) ===
  "wheel_alignment":        "WHEEL_ALIGNMENT_SERVICE",
  "wheel_alignment_check":  "WHEEL_ALIGNMENT_SERVICE",
  "four_wheel_alignment":   "WHEEL_ALIGNMENT_SERVICE",
  "4_wheel_alignment":      "WHEEL_ALIGNMENT_SERVICE",
  "front_wheel_alignment":  "WHEEL_ALIGNMENT_SERVICE",
  "thrust_alignment":       "WHEEL_ALIGNMENT_SERVICE",
  "alignment_check":        "WHEEL_ALIGNMENT_SERVICE",
  "geometry_check":         "WHEEL_ALIGNMENT_SERVICE",
  "tracking":               "WHEEL_ALIGNMENT_SERVICE",
  "uneven_tyre_wear":       "WHEEL_ALIGNMENT_SERVICE",
  "vehicle_pulling":        "WHEEL_ALIGNMENT_SERVICE",
  "steering_off_centre":    "WHEEL_ALIGNMENT_SERVICE"
};

const getRepairIntent = (viItemKey) => {
  if (!viItemKey) return null;
  const key = viItemKey.toLowerCase();
  if (REPAIR_INTENT_MAPPING[key]) return REPAIR_INTENT_MAPPING[key];
  for (const [pattern, intent] of Object.entries(REPAIR_INTENT_MAPPING)) {
    if (key.includes(pattern) || pattern.includes(key)) return intent;
  }
  return null;
};

// iter197g — Resolve repair intent from MCC category / canonical_rule_id
// / jcu_package_id when no VI item key is present. Required for
// MCC-driven and DVSA-imported job groups (e.g. shock absorber on
// WX12FYD — vi_item_key is null because the JG came from a DVSA MOT
// outcome, not a manual VI). Without this fallback, the inline JCE
// hint pills never render on MCC-derived pricing cards.
const MCC_CATEGORY_TO_INTENT = {
  tyre:            "TYRE_REPLACEMENT",
  brake:           "BRAKE_FRONT_OVERHAUL",
  suspension_arm:  "SUSPENSION_FRONT_ARM_REPLACEMENT",
  ball_joint:      "SUSPENSION_FRONT_ARM_REPLACEMENT",
  shock_absorber:  "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  coil_spring:     "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  wheel_bearing:   "SUSPENSION_FRONT_WHEEL_BEARING_REPLACEMENT",
  battery:         "BATTERY_REPLACEMENT",
  fluid_leak:      "ENGINE_OIL_LEAK_REPAIR",
  wheel_alignment: "WHEEL_ALIGNMENT_SERVICE",
  alignment:       "WHEEL_ALIGNMENT_SERVICE",
};

const CANONICAL_RULE_TO_INTENT = {
  rule_front_shocks:       "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  rule_rear_shocks:        "SUSPENSION_REAR_SHOCK_REPLACEMENT",
  rule_front_springs:      "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  rule_rear_springs:       "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  rule_front_top_mounts:   "SUSPENSION_FRONT_TOP_MOUNT_REPLACEMENT",
  rule_front_lower_arm:    "SUSPENSION_FRONT_ARM_REPLACEMENT",
  rule_ball_joints:        "SUSPENSION_FRONT_ARM_REPLACEMENT",
  rule_track_rod_ends:     "SUSPENSION_FRONT_TRACK_ROD_REPLACEMENT",
  rule_anti_roll_bar_links:"SUSPENSION_FRONT_DROP_LINK_REPLACEMENT",
  rule_wheel_bearings:     "SUSPENSION_FRONT_WHEEL_BEARING_REPLACEMENT",
  rule_tyre_replacement:   "TYRE_REPLACEMENT",
  // iter198 — was "WHEEL_ALIGNMENT_4WHEEL" which doesn't exist in
  // the backend seed; routes to the canonical JCE_WHEEL_ALIGNMENT
  // rule via WHEEL_ALIGNMENT_SERVICE.
  rule_wheel_alignment:    "WHEEL_ALIGNMENT_SERVICE",
};

const JCU_PACKAGE_TO_INTENT = {
  pkg_front_shocks:      "SUSPENSION_FRONT_SHOCK_REPLACEMENT",
  pkg_rear_shocks:       "SUSPENSION_REAR_SHOCK_REPLACEMENT",
  pkg_front_springs:     "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  pkg_rear_springs:      "SUSPENSION_FRONT_SPRING_REPLACEMENT",
  pkg_top_mount:         "SUSPENSION_FRONT_TOP_MOUNT_REPLACEMENT",
  pkg_lower_arm:         "SUSPENSION_FRONT_ARM_REPLACEMENT",
  pkg_ball_joint:        "SUSPENSION_FRONT_ARM_REPLACEMENT",
  pkg_track_rod_end:     "SUSPENSION_FRONT_TRACK_ROD_REPLACEMENT",
  pkg_arb_links:         "SUSPENSION_FRONT_DROP_LINK_REPLACEMENT",
  pkg_wheel_bearing:     "SUSPENSION_FRONT_WHEEL_BEARING_REPLACEMENT",
  pkg_front_brake_discs_pads: "BRAKE_FRONT_OVERHAUL",
  pkg_rear_brake_discs_pads:  "BRAKE_REAR_OVERHAUL",
};

const resolveIntent = ({ viItemKey, repairIntentCode, mccCategory, canonicalRuleId, jcuPackageId }) => {
  if (repairIntentCode) return repairIntentCode;
  const fromVi = getRepairIntent(viItemKey);
  if (fromVi) return fromVi;
  if (mccCategory && MCC_CATEGORY_TO_INTENT[mccCategory]) return MCC_CATEGORY_TO_INTENT[mccCategory];
  if (canonicalRuleId && CANONICAL_RULE_TO_INTENT[canonicalRuleId]) return CANONICAL_RULE_TO_INTENT[canonicalRuleId];
  if (jcuPackageId && JCU_PACKAGE_TO_INTENT[jcuPackageId]) return JCU_PACKAGE_TO_INTENT[jcuPackageId];
  return null;
};

const JCEV2InlineHints = ({ viItemKey, repairIntentCode, mccCategory, canonicalRuleId, jcuPackageId, className = "" }) => {
  const [hints, setHints] = useState([]);
  const [loading, setLoading] = useState(false);

  const effectiveIntent = resolveIntent({
    viItemKey,
    repairIntentCode,
    mccCategory,
    canonicalRuleId,
    jcuPackageId,
  });

  useEffect(() => {
    if (!effectiveIntent) {
      setHints([]);
      return;
    }

    const fetchHints = async () => {
      setLoading(true);
      try {
        const response = await axios.post(`${API}/jce-v2/inline-hints`, {
          repair_intent_code: effectiveIntent,
          max_hints: 3
        });
        setHints(response.data.hints || []);
      } catch (err) {
        console.error('Failed to fetch inline hints:', err);
        setHints([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHints();
  }, [effectiveIntent]);

  if (!effectiveIntent || hints.length === 0) return null;

  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="jce-inline-hints">
      {hints.map((hint, idx) => (
        <div
          key={idx}
          className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded border border-amber-200"
        >
          {hint.startsWith('✓') ? (
            <Check className="h-3 w-3 flex-shrink-0" />
          ) : (
            <Info className="h-3 w-3 flex-shrink-0" />
          )}
          <span className="truncate max-w-[200px]">{hint.replace('✓ ', '')}</span>
        </div>
      ))}
    </div>
  );
};

export default JCEV2InlineHints;
